import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { Alert, Snackbar } from "@mui/material";
import Slide from "@mui/material/Slide";
import { isNil } from "lodash";
import { usePostHog } from "posthog-js/react";

import {
  useAuthContext,
  useCarrierCompanyBookingLimits,
  useCarrierShipmentPageContext,
  useDisclosure,
  useShipmentQuotes,
  useSubscriptionStatus,
  useWriterComponentState,
} from "hooks";

import { MultiShipmentPricingModal } from "./components/MultiShipmentPricingModal";
import { MultiShipmentSubmitQuoteForm } from "./MultiShipmentSubmitQuoteForm";
import { showPricingAdvisoryModal } from "./utils";
import { APIErrorCode, ClientAppEvent } from "../../../../constants";
import {
  carrierHasLostShipment,
  extractDateFromIsoString,
  getSubmitQuoteBlockMessage,
  getSubmitQuoteBlockReason,
  naiveDateToISOString,
  startLessOrEqualToEnd,
} from "../../../../utils";

import type { SubmitMultiShipmentProjectQuoteFormState } from "./types";

const INIITAL_STATE: SubmitMultiShipmentProjectQuoteFormState = {
  equipmentType: "",
  pickupStart: "",
  pickupStartTime: "",
  deliveryEta: "",
  deliveryEtaTime: "",
  currency: "CAD",
  price: null,
  notes: "",
  trackingLinkOffered: false,
  minimumLoads: null,
  maxLoads: null,
};

interface Props {
  shipment: CarrierCondensedShipment;
  secondaryAction?: React.ReactNode;
}

export function MultiShipmentSubmitQuoteFormContainer(props: Props) {
  const { shipment, secondaryAction } = props;
  const { user } = useAuthContext();
  const { onRefresh } = useCarrierShipmentPageContext();
  const { refetchLimits } = useCarrierCompanyBookingLimits();
  const posthog = usePostHog();
  const [formValidationError, setFormValidationError] = useState<UIError>(null);

  const [formState, setFormState] =
    useState<SubmitMultiShipmentProjectQuoteFormState>(INIITAL_STATE);
  const shipmentRef = useRef<undefined | CondensedShipment>();
  const { subscription } = useSubscriptionStatus();
  const {
    isOpen: pricingAdivosryModalOpen,
    onClose: onClosePricingAdvisoryModal,
    onOpen: onOpenPricingAdvisoryModal,
  } = useDisclosure(false);

  const {
    isOpen: snackbarOpened,
    onClose: onSnackbarClose,
    onOpen: openSnackbar,
  } = useDisclosure(false);

  const {
    isOpen: carrierTeammatesOpened,
    onClose: closeCarrierTeammates,
    onOpen: openCarrierTeammates,
  } = useDisclosure(false);

  const { write, error, loading } = useWriterComponentState({
    errorMessage:
      "Failed to submit quote. Please contact us if the problem persists.",
    errorMessageKey: "message",
    writeConfig: {
      method: "post",
      url: `/shipments/${shipment.info.id}/quotes`,
      body: {
        equipment_type: formState.equipmentType,
        pickup_start:
          formState.pickupStart === ""
            ? ""
            : naiveDateToISOString(formState.pickupStart),
        pickup_start_time: null,
        delivery_eta:
          formState.deliveryEta === ""
            ? ""
            : naiveDateToISOString(formState.deliveryEta),
        delivery_eta_time: null,
        price: formState.price,
        currency: formState.currency,
        notes: formState.notes,
        tracking_link_offered: formState.trackingLinkOffered,
        min_loads_desired: formState.minimumLoads,
        max_loads_desired: formState.maxLoads,
        quoting_unit_of_measure: shipment.info.quoting_unit_of_measure,
      },
    },
    onWriteFailure: (e) => {
      const axiosError = e as { response?: { data?: { code?: string } } };
      const errorCode = axiosError?.response?.data?.code;
      if (errorCode === APIErrorCode.NO_CARRIER_INSURANCE) {
        posthog?.capture(ClientAppEvent.CARRIER_QUOTE_REJECTED_NO_INSURANCE, {
          shipment_id: shipment.info.id,
        });
      } else if (errorCode === APIErrorCode.EXPIRED_CARRIER_INSURANCE) {
        posthog?.capture(
          ClientAppEvent.CARRIER_QUOTE_REJECTED_EXPIRED_INSURANCE,
          {
            shipment_id: shipment.info.id,
          },
        );
      }
    },
  });

  const { quotes, isLoading: quoteRefetchLoading } = useShipmentQuotes(
    shipment?.info.id ?? 0,
  );
  const findCarrierActiveShipment = useCallback(
    (q: CondensedQuote) =>
      (q.internal_state === "ACTIVE" || q.internal_state === "COUNTERED") &&
      q.created_by.id === user?.id,
    [user?.id],
  );

  const hasActiveQuote = useMemo(
    () => quotes?.some((q) => findCarrierActiveShipment(q)) ?? false,
    [quotes, findCarrierActiveShipment],
  );
  const shipmentBookedElsewhere = carrierHasLostShipment(shipment);
  const canSubmitQuote = !hasActiveQuote && !shipmentBookedElsewhere;
  const submitBlockReason = getSubmitQuoteBlockReason(
    hasActiveQuote,
    shipmentBookedElsewhere,
  );

  useEffect(() => {
    if (shipment && shipmentRef.current?.info?.id !== shipment.info.id) {
      shipmentRef.current = shipment;
      setFormState(INIITAL_STATE);
      refetchLimits();
    }
  }, [shipment, refetchLimits]);

  useEffect(() => {
    if (
      shipment &&
      shipmentRef.current &&
      shipmentRef.current.version !== shipment.version
    ) {
      shipmentRef.current = shipment;
      refetchLimits();
    }
  }, [shipment, refetchLimits]);

  const onValueChanged = <T,>(
    key: keyof SubmitMultiShipmentProjectQuoteFormState,
    value: T,
  ): void => {
    setFormState((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const validateForm = () => {
    const { price, minimumLoads, maxLoads, pickupStart, deliveryEta } =
      formState;

    if (isNil(price) || price <= 0) {
      return "Price must be greater than 0.";
    }

    if (!startLessOrEqualToEnd(pickupStart, deliveryEta)) {
      return "Pickup start must be before or equal to Delivery ETA.";
    }

    if (formState.equipmentType === "") {
      return "Equipment type is required.";
    }

    if (isNil(minimumLoads) || minimumLoads < 1) {
      return "Minimum loads must be greater than 0.";
    }

    if (isNil(maxLoads) || maxLoads < 1) {
      return "Max loads must be greater than 0.";
    }

    if (minimumLoads > maxLoads) {
      return "Max loads must be greater than or equal to minimum loads.";
    }

    if (
      shipment.project?.loads_available !== undefined &&
      minimumLoads > shipment.project?.loads_available
    ) {
      return `Minimum loads must be less than or equal to available loads (${shipment.project?.loads_available}).`;
    }

    return null;
  };

  const refreshPage = () => {
    if (onRefresh) {
      onRefresh();
    }
  };

  const submitQuote = () => {
    // Re-check eligibility here, not just at onSubmit: the pricing advisory
    // modal sits between the two, so the carrier's quotes (or the shipment)
    // may have changed while it was open.
    if (!canSubmitQuote) {
      setFormValidationError(
        submitBlockReason != null
          ? getSubmitQuoteBlockMessage(submitBlockReason)
          : "You can no longer submit a quote on this shipment.",
      );
      return;
    }
    posthog?.capture(ClientAppEvent.QUOTE_SUBMITTED, {
      quote_type: "multishipment_project",
      shipment_id: shipment.info.id,
    });
    write(() => {
      refreshPage();
      setFormValidationError(null);
    });
  };

  const onSubmit = () => {
    setFormValidationError(null);
    const errors = validateForm();
    if (errors != null) {
      setFormValidationError(errors);
    } else {
      // case where pricing advise modal is not shown
      // eslint-disable-next-line
      if (
        subscription &&
        !showPricingAdvisoryModal(
          subscription.subscription_plan,
          shipment.is_partner_shipment,
        )
      ) {
        submitQuote();
      } else {
        onOpenPricingAdvisoryModal();
      }
    }
  };

  const onModalPricingCancelled = () => {
    onClosePricingAdvisoryModal();
  };

  // No refresh here: submitQuote already refreshes on success, and refreshing on a
  // rejection revalidates the shipment detail cache. A carrier the quote endpoint has
  // just refused is often one GET /shipments/{id}/condensed also refuses, so the refetch
  // replaced the detail pane — and the error explaining the rejection — with "Failed to
  // fetch shipment details".
  const onModalPricingSubmit = () => {
    onClosePricingAdvisoryModal();
    submitQuote();
  };

  const onPopulateShipmentRequirements = () => {
    onValueChanged(
      "pickupStart",
      extractDateFromIsoString(shipment.first_point.start_time),
    );
    onValueChanged(
      "deliveryEta",
      extractDateFromIsoString(shipment.last_point.start_time),
    );

    if (shipment.equipments.length > 0) {
      onValueChanged("equipmentType", shipment.equipments[0].type);
    }
    openSnackbar();
  };

  return (
    <>
      {pricingAdivosryModalOpen &&
        subscription &&
        showPricingAdvisoryModal(
          subscription.subscription_plan,
          shipment.is_partner_shipment,
        ) && (
          <MultiShipmentPricingModal
            onModalClose={onModalPricingCancelled}
            onAcknowledge={onModalPricingSubmit}
            isOpen={pricingAdivosryModalOpen}
          />
        )}
      <MultiShipmentSubmitQuoteForm
        onValueChanged={onValueChanged}
        onSubmit={onSubmit}
        error={error}
        loading={loading}
        canSubmitQuote={canSubmitQuote}
        submitBlockReason={submitBlockReason}
        formState={formState}
        quoteRefetchLoading={quoteRefetchLoading}
        formValidationError={formValidationError}
        onPopulateShipmentRequirements={onPopulateShipmentRequirements}
        carrierTeammatesOpened={carrierTeammatesOpened}
        closeCarrierTeammates={closeCarrierTeammates}
        openCarrierTeammates={openCarrierTeammates}
        shipment={shipment}
        secondaryAction={secondaryAction}
      />
      <Snackbar
        id="customerRequierementsPopulatedSnackbar"
        open={snackbarOpened}
        autoHideDuration={2000}
        onClose={onSnackbarClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        TransitionComponent={Slide}
      >
        <Alert
          onClose={onSnackbarClose}
          severity="success"
          sx={{ width: "100%" }}
        >
          Requirements applied!
        </Alert>
      </Snackbar>
    </>
  );
}
